import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PROPERTY_BY_SLUG } from "../data/properties";

// Lightweight client-side wishlist for saved homes. No accounts, no DB —
// persisted to localStorage so participants, families and coordinators can
// shortlist before enquiring.

const STORAGE_KEY = "alpha_saved_homes";
const SavedHomesCtx = createContext(null);

function readStorage() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch (e) {
    // Corrupted JSON — clear-and-recover gracefully so the UI never breaks.
    if (typeof window !== "undefined" && window.console) {
      console.warn("[savedHomes] Failed to parse storage; resetting.", e);
    }
    return [];
  }
}

function writeStorage(list) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // quota exceeded / private mode — silently noop
  }
}

export function SavedHomesProvider({ children }) {
  const [saved, setSaved] = useState(() => readStorage());

  // Persist on every change
  useEffect(() => {
    writeStorage(saved);
  }, [saved]);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setSaved(readStorage());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const isSaved = useCallback((slug) => saved.includes(slug), [saved]);

  const toggle = useCallback((slug) => {
    if (!slug) return;
    setSaved((cur) => (cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]));
  }, []);

  const remove = useCallback((slug) => {
    setSaved((cur) => cur.filter((s) => s !== slug));
  }, []);

  const clear = useCallback(() => setSaved([]), []);

  // Resolve slugs to the full property objects in the registry (in saved-order)
  const items = useMemo(
    () => saved.map((slug) => PROPERTY_BY_SLUG[slug]).filter(Boolean),
    [saved]
  );

  const value = useMemo(
    () => ({ saved, items, count: items.length, isSaved, toggle, remove, clear }),
    [saved, items, isSaved, toggle, remove, clear]
  );

  return <SavedHomesCtx.Provider value={value}>{children}</SavedHomesCtx.Provider>;
}

export const useSavedHomes = () => {
  const ctx = useContext(SavedHomesCtx);
  if (!ctx) throw new Error("useSavedHomes must be used inside SavedHomesProvider");
  return ctx;
};
